<script setup lang="ts">
/**
 * LyricEntryOverlay
 *
 * Schwebendes Eingabefeld für den **Gesangstext** (MuseScore-Stil): Note wählen, tippen,
 * `Leertaste` rückt weiter. Verankert **unterhalb** der primären Note (`selectedNoteX/Y` aus dem
 * `ScoreEditContext`), je Strophe eine Zeile tiefer, damit es die Silbe nicht verdeckt.
 *
 * Die Komponente **schreibt nichts selbst** – sie bedient `commitLyric`/`endLyricEntry` der Sitzung
 * (`useLyricEntry`), die den lokalen `musicXml`-Ref pflegt und erst am Sitzungsende nach oben meldet.
 *
 * `@keydown.stop` auf der Box ist Pflicht: die Handler für Leertaste (Wiedergabe) sowie Strg+Z/Y
 * (Undo/Redo) in `MusicXmlPreview` haben **keinen** `isEditingText()`-Schutz – ohne das Stoppen
 * würde jede getippte Leertaste die Wiedergabe starten. Esc läuft über `useCaptureEscape`
 * (Capture-Phase), damit die Escape-Taste der `ScoreEditToolbar` nicht zusätzlich die Notenauswahl
 * aufhebt.
 */
import {computed, nextTick, ref, watch} from 'vue';
import {useElementSize} from '@vueuse/core';
import {Icon} from '@iconify/vue';

import {injectScoreEditContext} from './editContext';
import {clampedPosition, PANEL_MARGIN} from './panelPosition';
import {useCaptureEscape} from '@/composables/useCaptureEscape';
import {focusWithoutScroll} from '@/shared/focus';
import {BASS_LYRIC_NUMBER, EDITABLE_VERSE_NUMBERS} from '@/composables/musicXmlDom';
import type {VerseNumber} from '@/composables/osmd/useLyricEntry';

/** Grundabstand unter der Note (px); je Strophe eine Textzeile tiefer. */
const BELOW_NOTE_OFFSET = 44;
const VERSE_ROW_HEIGHT = 18;
/** Slot 3 trägt die Bass-Zeile der Griffschrift – als gesperrter Chip sichtbar, damit die
 *  Einschränkung sich selbst erklärt, statt still zu verschwinden. */
const RESERVED_VERSE_HINT = 'Strophe 3 ist für die Bass-Zeile der Griffschrift reserviert';

const {
  selectedNoteX, selectedNoteY, selectedNote,
  activeVerse, isLyricActive, lyricDraft, commitLyric, endLyricEntry,
} = injectScoreEditContext();

// ============ Position (analog BassLyricPopover, aber unterhalb der Strophenzeile) ============

const panelRef = ref<HTMLElement | null>(null);
const {width: panelWidth, height: panelHeight} = useElementSize(panelRef);

const panelTop = computed(() => {
  const h = panelHeight.value || 0;
  const extent = panelRef.value?.parentElement?.clientHeight ?? 0;
  const offset = BELOW_NOTE_OFFSET + VERSE_ROW_HEIGHT * (Number(activeVerse.value) - 1);
  const anchor = selectedNoteY.value === null ? null : selectedNoteY.value + offset + h / 2;
  return clampedPosition(anchor, h, extent, PANEL_MARGIN);
});

const panelLeft = computed(() => {
  const w = panelWidth.value || 0;
  const extent = panelRef.value?.parentElement?.clientWidth ?? 0;
  return clampedPosition(selectedNoteX.value, w, extent, Math.max(PANEL_MARGIN, extent - w - PANEL_MARGIN));
});

// ============ Fokus ============

const inputRef = ref<HTMLInputElement | null>(null);

async function focusInput(): Promise<void> {
  await nextTick();
  if (document.activeElement !== inputRef.value) {
    focusWithoutScroll(inputRef.value);
    inputRef.value?.select();
  }
}

watch(isLyricActive, (active) => {
  if (active) void focusInput();
}, {immediate: true});

// Ein Klick in die Partitur zieht den Fokus auf den Container – bei laufender Sitzung zurückholen,
// damit die nächste getippte Silbe wieder im Feld landet.
watch(selectedNote, () => {
  if (isLyricActive.value) void focusInput();
});

// ============ Tastatur ============

/** Ein einziger Handler statt Vue-Key-Modifier: `-`/`_` sind als Modifier-Namen nicht verlässlich,
 *  und der Bindestrich braucht ohnehin eine Cursor-Prüfung. Pfeile, Backspace und Entf bleiben
 *  bewusst unbehandelt (nativer Textcursor). */
function onKeydown(event: KeyboardEvent): void {
  const input = event.target as HTMLInputElement;
  if (event.key === ' ' || event.key === 'Tab' || event.key === 'ArrowRight') {
    event.preventDefault();
    event.stopImmediatePropagation();
    commitLyric(event.shiftKey ? 'back' : 'space');
    return;
  }
  if (event.key === 'Enter') {
    event.preventDefault();
    endLyricEntry();
    return;
  }
  if (event.key === '_') {
    event.preventDefault();
    commitLyric('melisma');
    return;
  }
  // `-` schließt die Silbe nur ab, wenn der Cursor am Ende steht – sonst bleibt es ein normales
  // Zeichen, damit ein Bindestrich innerhalb einer Silbe tippbar bleibt.
  if (event.key === '-') {
    const atEnd = input.selectionStart === input.value.length && input.selectionStart === input.selectionEnd;
    if (!atEnd) return;
    event.preventDefault();
    commitLyric('hyphen');
  }
}

function pickVerse(verse: VerseNumber): void {
  activeVerse.value = verse;
  void focusInput();
}

useCaptureEscape(isLyricActive, endLyricEntry);
</script>

<template>
  <!-- Volle Fläche über der Partitur, transparent/pointer-events-none; nur die Box ist interaktiv. -->
  <div v-if="isLyricActive" class="pointer-events-none absolute inset-0 z-30">
    <div
        ref="panelRef"
        role="dialog"
        aria-label="Lyrics eingeben"
        class="pointer-events-auto absolute w-72 rounded-xl border border-gray-200 bg-white/95 p-3 shadow-lg backdrop-blur-sm animate-fade animate-duration-150"
        :style="{ top: `${panelTop}px`, left: `${panelLeft}px` }"
        @keydown.stop
    >
      <div class="mb-2 flex items-center gap-2">
        <Icon icon="ic:baseline-lyrics" class="text-rose-600"/>
        <span class="text-sm font-semibold text-gray-800">Lyrics</span>
        <div class="ml-auto flex items-center gap-1">
          <button
              v-for="verse in EDITABLE_VERSE_NUMBERS"
              :key="verse"
              type="button"
              class="h-6 min-w-6 rounded-md border px-1.5 text-xs transition-colors"
              :class="activeVerse === verse
                ? 'border-rose-300 bg-rose-50 font-semibold text-rose-700'
                : 'border-gray-200 bg-gray-50 text-gray-600 hover:border-rose-200 hover:bg-rose-50'"
              :title="`Strophe ${Number.parseInt(verse)!-Number.parseInt(BASS_LYRIC_NUMBER)!}`"
              :aria-pressed="activeVerse === verse"
              @click="pickVerse(verse)"
          >
            {{ Number.parseInt(verse)! - Number.parseInt(BASS_LYRIC_NUMBER)! }}
          </button>
          <!--          <button-->
          <!--              type="button"-->
          <!--              disabled-->
          <!--              class="h-6 min-w-6 cursor-not-allowed rounded-md border border-dashed border-gray-200 px-1.5 text-xs text-gray-300"-->
          <!--              :title="RESERVED_VERSE_HINT"-->
          <!--              :aria-label="RESERVED_VERSE_HINT"-->
          <!--          >-->
          <!--            -->
          <!--          </button>-->
        </div>
        <button
            type="button"
            class="flex h-6 w-6 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-600"
            title="Beenden (Esc)"
            aria-label="Beenden"
            @click="endLyricEntry"
        >
          <Icon icon="fa6-solid:xmark"/>
        </button>
      </div>

      <input
          ref="inputRef"
          v-model="lyricDraft"
          type="text"
          class="w-full rounded-lg border border-gray-300 px-2 py-1.5 text-base text-gray-900 outline-none focus:border-rose-400 focus:ring-2 focus:ring-rose-200"
          placeholder="Silbe tippen…"
          aria-label="Silbe"
          @keydown="onKeydown"
      >

      <p class="mt-2 text-[11px] leading-relaxed text-gray-500">
        <kbd class="rounded border border-gray-200 bg-gray-50 px-1 font-mono text-[10px] text-gray-600">Leertaste</kbd> nächste Note ·
        <kbd class="rounded border border-gray-200 bg-gray-50 px-1 font-mono text-[10px] text-gray-600">−</kbd> Silbentrennung ·
        <kbd class="rounded border border-gray-200 bg-gray-50 px-1 font-mono text-[10px] text-gray-600">_</kbd> Melisma ·
        <kbd class="rounded border border-gray-200 bg-gray-50 px-1 font-mono text-[10px] text-gray-600">⇧ Leertaste</kbd> zurück ·
        <kbd class="rounded border border-gray-200 bg-gray-50 px-1 font-mono text-[10px] text-gray-600">Esc</kbd> fertig
      </p>
    </div>
  </div>
</template>
